import { useSuspenseQuery } from '@tanstack/react-query';
import { userSettingsQuery } from '../api/users-api';
import { tiers } from '../shared/gPanel-tiers';
import {
  gPanel_Tier,
  MarketingOverviewModel,
  UserTier,
  ViewType,
} from '../../types';
import { FeatureFlagName } from './feature-flags';

interface UserSettings {
  userRoles: string[];
  calendarRoles: string[];
  driveRoles: string[];
  administrationRoles: string[];
  directoryRoles: string[];
  gPanelContactsRoles: string[];
  changeLogRoles: string[];
  billingRoles: string[];
  toolsRoles: string[];
  rulesEngineRoles: string[];
  currentTier: gPanel_Tier;
  usersTier: UserTier;
  loggedInUser: string;
  primaryDomain: string;
  canSeePasswords: boolean;
  limitedVisEnabled: boolean;
  limitedVisDomainList: string[];
  limitedVisOrgList: string[];
  bookmarks: string[];
  qbInstance: 'us' | 'canada';
  features: Record<FeatureFlagName, boolean>;
  isPlatform: boolean;
  userViewRoles: string[];
  apiUrl: string;
  allDomains: string[];
  activeLabs: string[];
  adminEmail: string;
  emailDomain: string;
  viewType: ViewType;
  timeout: number;
  userName: string;
  roleName: string;
  marketingOverview: MarketingOverviewModel | null;
  roleType: 'admin' | 'user';
  isDefault: boolean;
  firebaseDbPath: string;
  isFirstGpanelLogin: boolean;
  hasRoleWithLimitedVisibility: boolean;
  customerId: string;
  changeLogLength: number;
}

export const DEFAULT_VIEW_TYPE: ViewType = {
  // not used
  value: 'RichContent',
  umLayout: 'grid',
  orgUnitLayout: 'grid',
  buttonMode: 'ICON',
  isReactView: true,
  // used
  expandedNav: true,
  colorMode: 'light',
  bookmarks: [],
  reportsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  policiesGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  contactSyncsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  bulkOpsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  gmailDeletionsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  gmailExportsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  apisGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  driveSweepsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  signatureTemplatesGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  rulesEnginesGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  slackChannelsGridStates: null,
  userRolesGridStates: null,
  driveSearchesGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  calendarTransferLogsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  ownershipTransferLogsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  groupTemplatesGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  calendarsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  resourceCalendarsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  buildingsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  resourceTemplatesGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  userManagementIndexedGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  userManagementGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  labelManagerGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  groupsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  groupsIndexedGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  chromeDevicesGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  mobileManagementGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  mobileManagementIndexedGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  orgUnitsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  orgUnitsIndexedGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  sharedDrivesGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  sharedDrivesIndexedGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  loggingGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  cpanelLogsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  sharedContactsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  gpanelContactsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  trashedContactLabelsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  trashedContactsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  decomPoliciesGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  decomSuspendedUsersGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  decomLogsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  vaultMattersGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
  vaultExportsGridStates: {
    activeGridState: 'default',
    savedGridStates: [],
  },
};

export function useUserSettings(): UserSettings {
  const { data } = useSuspenseQuery(userSettingsQuery());

  return {
    userRoles: data.role?.userRoles || [],
    calendarRoles: data.role?.calendarRoles || [],
    driveRoles: data.role?.driveRoles || [],
    administrationRoles: data.role?.administrationRoles || [],
    directoryRoles: data.role?.directoryRoles || [],
    gPanelContactsRoles: data.role?.gPanelContactsRoles || [],
    changeLogRoles: data.role?.changeLogs || [],
    billingRoles: data.role?.billingRoles || [],
    toolsRoles: data.role?.toolsRoles || [],
    rulesEngineRoles: data.role?.rulesEngineRoles || [],
    currentTier: data.currentTier || 'essentials',
    usersTier:
      tiers.find((tier) => tier.value === data.currentTier) || tiers[0],
    loggedInUser: data.loggedInUser || '',
    primaryDomain: data.domainInfo?.primaryDomain || '',
    canSeePasswords: data.role?.canSeePasswords || false,
    limitedVisEnabled: data.role?.limitedVisEnabled || false,
    limitedVisDomainList: data.role?.limitedVisDomainList || [],
    limitedVisOrgList: data.role?.limitedVisOrgList || [],
    bookmarks: data.viewType?.bookmarks ?? DEFAULT_VIEW_TYPE.bookmarks,
    qbInstance: data.subscription?.qbInstance || 'us',
    features: data.features || {},
    isPlatform: data.isPlatform || false,
    userViewRoles: data.role?.userViewRoles || [],
    apiUrl: data.apiUrl || '',
    allDomains: data.domainInfo?.allDomains || [],
    activeLabs: data.trustedTester?.featureAccess || [],
    adminEmail: data.subscription?.adminEmail || '',
    emailDomain: data.subscription?.emailDomain || '',
    viewType: data.viewType ?? DEFAULT_VIEW_TYPE,
    timeout: data.subscription?.timeout || 15,
    userName: data.userName || '',
    roleName: data.role?.roleName || '',
    marketingOverview: data.marketingOverview,
    roleType: data.role?.roleType || 'user',
    isDefault: data.role?.default || false,
    firebaseDbPath: data.firebaseDbPath || '',
    isFirstGpanelLogin: data.isFirstGpanelLogin || false,
    hasRoleWithLimitedVisibility: data.hasRoleWithLimitedVisibility || false,
    customerId: data.subscription?.customerId || '',
    changeLogLength: data.role?.changeLogLength || 75,
  };
}
